import type { InstallPlatform } from '../hooks/useInstallPrompt'

/**
 * **D320 — which platforms are currently offered the move to the store-delivered app, and the one
 * line that switches a platform back on.**
 *
 * D282 raised the offer on Android and D313 widened it to iOS. Android's door is a *closed* test:
 * `beta.selogate.com` walks a resident through joining the testers group before Play will serve
 * the build, so a resident who taps *Show me* meets a multi-step enrolment rather than an install.
 * That is the right page while the testing is closed and the wrong campaign to run at launch
 * volume, so **the Android arm is withdrawn until the Play listing is open**, at which point the
 * same prompt and the same page become an ordinary install.
 *
 * **The list is the whole switch.** Adding `'android'` back restores D282's audience exactly —
 * nothing else in `betaVisible` moved, the `{{store}}` mark and the URL still come off D313's one
 * platform test in `App.tsx`, and `betaOffer.test.ts` renders the restored list so the path back
 * is proven rather than assumed.
 *
 * **Why a list rather than deleting the term.** `platform === 'other'` was D313's load-bearing
 * exclusion (a desktop browser has no store build to move to) and `!isNativeContainer()` is
 * D282's; collapsing the platform half to `platform === 'ios'` would read as *this offer is an
 * iOS feature*, which is the sentence that makes the Android arm hard to find again.
 *
 * **D338 put Android back on before Play's test opened**, reversing D320 on a different ground
 * than the one it waited for: the beta page now serves `?flow=migrate`, written for a resident who
 * already has the product, and `androidBetaUrl()` opens that flow. The closed test's join-and-wait
 * is still there and is accepted rather than removed. The list stays a list, so withdrawing an arm
 * again is the same one line.
 *
 * **D349 withdraws Android again, and the flip condition is now the PUBLIC listing.** The owner
 * cancels the unsolicited offer until the Play Store listing is open to anyone, at which point the
 * tap should land on an install rather than a join-and-wait — so re-enabling it is this line *and*
 * a look at whether `androidBetaUrl()` should still be the door. The migrate flow, the host and the
 * copy are untouched; only the offer to a resident already in the PWA is withdrawn.
 */
export type BetaOfferPlatform = Exclude<InstallPlatform, 'other'>

/** iOS alone since D349. Add `'android'` back when the Play listing is public — D338 did, once. */
export const BETA_OFFER_PLATFORMS: readonly BetaOfferPlatform[] = ['ios']

/**
 * The platform half of `betaVisible`'s gate. `enabled` is a parameter only so the suite can render
 * the restored list — no caller passes it, and `App.tsx` reads the constant above.
 */
export function isBetaOfferPlatform(
  platform: InstallPlatform,
  enabled: readonly BetaOfferPlatform[] = BETA_OFFER_PLATFORMS,
): boolean {
  return platform !== 'other' && enabled.includes(platform)
}
